<template>
  <v-dialog
    v-bind:value="value"
    v-on:input="onCancel"
    width="500px">
    <form class="form-add-break" v-on:submit.prevent="onSubmit">
      <h3 class="">{{ title }}</h3>
      <div class="form-add-break__name">{{ employeeName }}</div>
      <break-period
        class="form-add-break__row"
        v-for="(range, index) in timeRangeList"
        v-bind:key="range.key"
        v-bind:can-add="timeRangeList.length === index + 1"
        v-bind:can-remove="index !== 0"
        v-bind:timeline-from="timeline.from"
        v-bind:timeline-to="timeline.to"
        v-bind:value="range"
        hide-details
        v-on:add-range="addNewTimeRangeBreak"
        v-on:input="onInputRange($event, index, range.key)"
        v-on:remove-range="removeTimeRangeBreak(index)"
      />
      <textarea-length-limitation v-model="note" v-bind:maxLength="100" placeholder="Примечание" />
      <div class="form-add-break__buttons">
        <button class="form-add-break__cancel" type="button" v-on:click="onCancel">Отмена</button>
        <button
          class="form-add-break__submit"
          type="submit"
          v-bind:disabled="isDisabled">
          Добавить
        </button>
      </div>
    </form>
  </v-dialog>
</template>

<script>
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TimeAutocomplete from '@/components/TimeAutocomplete.vue';

/**
 * @description Компонент периода.
 * @type {Vue}
 */
const BreakPeriod = {
  components: { TimeAutocomplete },
  computed: {
    fromTimeline({ timelineFrom, value }) {
      const { from } = value;
      if (from) {
        return [...timelineFrom, { datetime: from, title: from }];
      }
      return timelineFrom;
    },
    toTimeline({ timelineTo, value }) {
      let items = timelineTo.slice();
      if (value.from) {
        items = items.filter((item) => item.title > value.from);
      }
      if (value.to) {
        items.push({ datetime: value.to, title: value.to });
      }
      return items;
    },
  },
  name: 'BreakRange',
  props: {
    canAdd: { type: Boolean, required: true },
    canRemove: { type: Boolean, required: true },
    hideDetails: { type: Boolean, required: false },
    timelineFrom: { type: Array, required: true },
    timelineTo: { type: Array, required: true },
    value: { type: Object, required: true },
  },
  render(createElement) {
    const timePeriodButtons = [];
    const { from, to } = this.value;
    const emitInput = (newFrom, newTo) => this.$emit('input', { from: newFrom, to: newTo });
    const makeTime = (placeholder, value, timeline, onInput, hideDetails) => createElement(
      TimeAutocomplete,
      {
        on: { input: onInput },
        props: {
          placeholder,
          timeline,
          value,
          hideDetails,
        },
        staticClass: 'form-time-range__input',
      },
    );
    const makeButton = (text, type, title) => createElement(
      'button',
      {
        attrs: { type: 'button', title },
        on: { click: () => this.$emit(`${type}-range`) },
        staticClass: `form-add-break__button-${type}`,
      },
      text,
    );
    if (this.canAdd) timePeriodButtons.push(makeButton('', 'add', 'Добавить'));
    if (this.canRemove) timePeriodButtons.push(makeButton('', 'remove', 'Удалить'));
    const buttonsWrapper = timePeriodButtons.length
      ? createElement(
        'div',
        {
          staticClass: 'form-add-break__buttons-wrapper',
        },
        timePeriodButtons,
      )
      : null;
    return createElement(
      'div',
      [
        makeTime('С', from, this.fromTimeline, (input) => emitInput(input, to), this.hideDetails),
        '—',
        makeTime('По', to, this.toTimeline, (input) => emitInput(from, input), this.hideDetails),
        buttonsWrapper,
      ],
    );
  },
};

/**
 * @description Компонент диалога добавления перерыва сотруднику.
 * @type {Vue}
 */
export default {
  components: { TextareaLengthLimitation, BreakPeriod },
  computed: {
    isDisabled: ({ timeRangeList }) => timeRangeList.find(
      (range) => !range.from || !range.to || range.from > range.to,
    ),
  },
  data() {
    return {
      timeRangeList: [{ from: '', to: '', key: Date.now() }],
      note: '',
    };
  },
  maskTime: '##:##',
  methods: {
    /**
     * @description Добавить новый временной отрезок в перерыв.
     * @returns {void}
     */
    addNewTimeRangeBreak() {
      this.timeRangeList.push({ from: '', to: '', key: Date.now() });
    },
    /**
     * @description Обработать ввод времени.
     * @param {{from:string,to:string}} range
     * @param {number}                  index
     * @param {number}                  key
     * @returns {void}
     */
    onInputRange(range, index, key) {
      this.$set(this.timeRangeList, index, { ...range, key });
    },
    /**
     * @description Отменить.
     * @returns {void}
     */
    onCancel() {
      this.$emit('cancel');
    },
    /**
     * @description Подтвердить.
     * @returns {void}
     */
    onSubmit() {
      const request = { time_range_list: this.timeRangeList, note: this.note };
      this.$emit('submit', request);
    },
    /**
     * @description Удалить временной отрезок из перерыва.
     * @param {number} index Индекс временного отрезка.
     * @returns {void}
     */
    removeTimeRangeBreak(index) {
      this.$delete(this.timeRangeList, index);
    },
  },
  name: 'DialogAddEmployeeBreak',
  props: {
    /**
     * @description Имя сотрудника.
     */
    employeeName: { type: String, required: true },
    /**
     * @description Временная шкала.
     */
    timeline: { type: Object, required: true },
    /**
     * @description Заголовок.
     */
    title: { default: () => 'Добавить перерыв', type: String },
    /**
     * @description Показывать диалог.
     */
    value: { required: true, type: Boolean },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.form-add-break {
  $button-font-size: 16px;
  $button-height: 45px;
  $button-width: 100%;

  background: #fff;
  padding: 15px 20px 27px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  &__name {
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #192a3e;
  }
  &__row {
    display: grid;
    grid-template-columns: minmax(70px, 105px) 10px minmax(70px, 105px) 1fr;
    gap: 16px 10px;
    align-items: center;
  }
  &__input {
    border: 1px solid #c2cfe0;
    box-sizing: border-box;
    border-radius: 4px;
    height: 40px;
    width: 100%;
    padding: 10px;
    color: #707683;
    font-size: 16px;
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0 !important;
      outline: 0;
    }
    &:disabled {
      background-color: #fff;
    }
  }
  &__buttons-wrapper {
    display: flex;
    column-gap: 10px;
    button {
      flex-shrink: 0;
    }
  }
  &__button {
    &-add {
      text-align: left;
      color: #0489e0;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      @include buttons.btn-plus-color-add;
    }
    &-remove {
      text-align: left;
      color: var(--warning-color);
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      @include buttons.btn-cross-color-remove;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
  }
  &__cancel {
    @include buttons.btn-transparent($button-width, $button-height, $button-font-size);
  }
  &__submit {
    @include buttons.btn-blue($button-width, $button-height, $button-font-size);
    &:disabled {
       background-color: var(--unfocus-color);
    }
  }
}

.form-time-range {
  &__input {
    &.v-input--hide-details {
      margin: 0;
      padding: 0;
    }
  }
}

@media screen and (max-width: 480px) {
  .form-add-break {
    &__row {
      grid-template-columns: minmax(70px, 1fr) 10px minmax(70px, 1fr) 70px;
    }
  }
}
</style>
